import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { Grid, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { PageContentExterneDto } from '../../../services/dtos/page-content-externe.dto';
import { getFieldContent } from '../../../services/page.service';
import axios, { AxiosError } from 'axios';
import { httpCodeNotFound } from '../../../utils/constants.utils';
import { showAxiosMessageError } from '../../../services/error-message.service';
import EditContent from '../../../components/others/edit-content/edit-content';
import { ArrowBack } from '@mui/icons-material';

/**
 * EditContentPage component.
 * Permet la modif d'un champ de contenu d'une page
 *
 * @return {React.Element} The rendered EditContentPage component.
 */
export default function EditContentPage(): JSX.Element {
    // Récup l'id du champ passé dans l'URL
    const { id } = useParams();

    // SnackBar pour les messages
    const showMessage = useSnackbars();

    // Navigate
    const navigate = useNavigate();

    // Permet de savoir si on est passé une fois dans l'useEffect
    const dataFetchedRef = useRef(false);

    // Etat de récup des données
    const [loading, setLoading] = useState<boolean>(true);

    // Champ de contenu
    const [fieldContent, setFieldContent] = useState<PageContentExterneDto | undefined>(undefined);

    // Récupère le champ via l'API
    useEffect(() => {
        if (dataFetchedRef.current) {
            return;
        }
        dataFetchedRef.current = true;

        getFieldContent(id)
            .then(result => {
                setFieldContent(result);
            })
            .catch((error: Error | AxiosError) => {
                if (axios.isAxiosError(error)) {
                    if (error?.response?.status === httpCodeNotFound) {
                        navigate('/erreur-404');
                    } else {
                        showAxiosMessageError(error, showMessage);
                    }
                } else {
                    showAxiosMessageError(error, showMessage);
                }
            })
            .finally(() => {
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Retourne la vue, en passant par le component EditContent
    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                {loading ? (
                    <Skeleton variant={'text'}>
                        <Tooltip title={'Retourner à la liste des contenus'}>
                            <IconButton component={Link} to={'/administration/pages'}>
                                <ArrowBack></ArrowBack>
                            </IconButton>
                        </Tooltip>
                        <Typography variant={'h5'}>Modifier le contenu du champ de la page</Typography>
                    </Skeleton>
                ) : (
                    <Typography variant={'h5'}>
                        <Tooltip title={'Retourner à la liste des contenus'}>
                            <IconButton component={Link} to={'/administration/pages'}>
                                <ArrowBack></ArrowBack>
                            </IconButton>
                        </Tooltip>
                        Modifier le contenu du champ {fieldContent?.codeChamps} de la page {fieldContent?.codePage}
                    </Typography>
                )}
            </Grid>
            <Grid item xs={12}>
                {loading || fieldContent === undefined ? (
                    <Skeleton variant={'rectangular'} />
                ) : (
                    <EditContent fieldContent={fieldContent} />
                )}
            </Grid>
        </Grid>
    );
}
